import { OtherManualSeasonDistributionView } from 'components/OtherManualSeasonDistribution/';
import { RequestStatus } from 'features/request/requestSlice';
import type { ConstructionTypeData, ContractOtherCostLimitData } from 'gql/graphql';
import { LimitDistributionMethodEnum, ProjectMethodologyEnum } from 'gql/graphql';
import type { TableColumn } from 'uikit';
import { Checkbox } from 'uikit';
import { formatNumberToRu } from 'utils';

import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { DistributionMethodMap } from '../constants';

import { useConstructionTypes } from './useConstructionTypes';
import { useOtherData } from './useOtherData';

import Styles from './useOtherViewModal.module.css';

type TableRow = {
    id: string;
    constructionType: string;
    checked: boolean;
    sum: string | null;
    distributionMethod: string | null | undefined;
    manualLimitValues: ContractOtherCostLimitData[] | undefined;
};

interface IUseOtherViewModal {
    constructionTypes?: ConstructionTypeData[];
    contractId?: number;
    contractOtherCostId?: number;
    methodology: ProjectMethodologyEnum;
    workOrderOtherId?: number;
    workOrderChangeOtherId?: number;
    costRounding: number;
    otherId?: number;
}

export const useOtherViewModal = ({
    constructionTypes,
    contractId,
    contractOtherCostId,
    methodology,
    workOrderOtherId,
    workOrderChangeOtherId,
    costRounding,
}: IUseOtherViewModal) => {
    const { orderId, otherId, versionId, orderChangeId } = useParams();
    const isVersionPage = !!versionId;
    const isWorkOrderChange = !!orderChangeId;

    const {
        data,
        otherCostLimits,
        contractOtherStatus,
        workOrderOtherStatus,
        getWorkOrderChangeStatus,
        manualLimitValues,
    } = useOtherData({
        contractId,
        contractOtherCostId: contractOtherCostId ?? Number(otherId),
        methodology,
        workOrderOtherId,
        workOrderChangeOtherId,
        isVersionPage,
        isWorkOrderChange,
    });
    const { constructionTypesData, constructionTypesStatus } = useConstructionTypes({ methodology, orderId });

    const columns = useMemo<TableColumn<TableRow>[]>(
        () => [
            {
                title: 'Вид строительства',
                accessor: 'constructionType',
                renderCell: ({ constructionType, checked }) => (
                    <Checkbox
                        className={Styles.checkbox}
                        checked={checked}
                        label={constructionType}
                        disabled
                        size="m"
                    />
                ),
                withoutPadding: true,
            },
            { title: 'Сумма, руб. без НДС', accessor: 'sum', withoutPadding: true },

            {
                title: 'Способ распределения',
                accessor: 'distributionMethod',
                renderCell: ({ distributionMethod, constructionType, manualLimitValues: limits, sum }) => (
                    <div className={Styles.distributionMethodCell}>
                        {distributionMethod}

                        <div className={Styles.distributionMethodBtn}>
                            {distributionMethod === DistributionMethodMap[LimitDistributionMethodEnum.Manual] && (
                                <OtherManualSeasonDistributionView
                                    limits={limits}
                                    costRounding={costRounding}
                                    constructionTypeName={constructionType}
                                    constructionTypeCost={sum}
                                />
                            )}
                        </div>
                    </div>
                ),
                withoutPadding: true,
                hidden: methodology === ProjectMethodologyEnum.Bim,
            },
        ],
        [costRounding, methodology],
    );

    const rows = useMemo<TableRow[]>(() => {
        const constructionTypesSource = constructionTypes ?? constructionTypesData;

        return constructionTypesSource
            .filter((type): type is NonNullable<typeof type> => type !== null)
            .map(({ name, id }) => {
                const limit = otherCostLimits.find(({ constructionType }) => constructionType.id === id);

                return {
                    constructionType: name,
                    id: String(id),
                    sum: limit?.cost ? formatNumberToRu(Number(limit.cost), costRounding) : null,
                    distributionMethod: limit?.distributionMethod
                        ? DistributionMethodMap[limit.distributionMethod]
                        : null,
                    checked: !!limit,
                    manualLimitValues: manualLimitValues?.filter(({ constructionType }) => constructionType?.id === id),
                };
            });
    }, [constructionTypes, constructionTypesData, costRounding, manualLimitValues, otherCostLimits]);

    return useMemo(
        () => ({
            data,
            columns,
            rows,
            isLoading: [
                contractOtherStatus,
                workOrderOtherStatus,
                constructionTypesStatus,
                getWorkOrderChangeStatus,
            ].includes(RequestStatus.FETCHING),
        }),
        [
            columns,
            constructionTypesStatus,
            contractOtherStatus,
            data,
            getWorkOrderChangeStatus,
            rows,
            workOrderOtherStatus,
        ],
    );
};
